import { useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ApiError, api } from '../api/client';
import { CZK } from '../lib/format';
import { longDt } from '../lib/filters';

/**
 * Dobropis — the legacy `printnote`: the STORNO of a receipt, whole
 * (`?druh=full`), services only (`services`) or the late penalty only
 * (`price_penalty`). Opening it issues the PI99 number once and re-reads it
 * after; prints itself unless `?tisk=0`.
 */
const kc = (n: number) => `${CZK.format(n)} Kč`;

export function Dobropis() {
  const { id } = useParams();
  const rid = Number(id);
  const [sp] = useSearchParams();
  const preview = sp.get('tisk') === '0';
  const kindParam = sp.get('druh');
  const kind = kindParam === 'services' || kindParam === 'price_penalty' ? kindParam : 'full';
  const m = useMutation({ mutationFn: () => api.creditNote(rid, kind) });
  const fired = useRef(false);
  useEffect(() => {
    if (fired.current || !Number.isInteger(rid) || rid <= 0) return;
    fired.current = true;
    m.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rid]);
  useEffect(() => {
    if (!m.data || preview) return;
    const t = window.setTimeout(() => window.print(), 250);
    return () => window.clearTimeout(t);
  }, [m.data, preview]);

  if (!Number.isInteger(rid) || rid <= 0) return <div className="container state">Neplatné číslo rezervace.</div>;
  if (m.isPending || m.isIdle) return <div className="container state">Připravuji dobropis…</div>;
  if (m.isError || !m.data) {
    const e = m.error;
    return <div className="container state">{e instanceof ApiError ? e.serverMessage ?? e.message : 'Dobropis se nepodařilo vystavit.'}</div>;
  }
  const d = m.data;
  const r = d.reservation;
  return (
    <div className="protokol uctenka">
      <div className="controls noprint">
        <button type="button" className="btn" onClick={() => window.print()}>
          {preview ? 'Tisk' : 'Opakovat tisk'}
        </button>
        <button type="button" className="btn" onClick={() => window.close()}>
          Zavřít okno
        </button>
      </div>
      <div className="sheet">
        <div className="grid2">
          <div>
            <p>
              <strong>Dodavatel:</strong>
              <br />
              Smile Parking s.r.o.
              <br />
              U Špejcharu 504, Tuchoměřice, 252 67
              <br />
              IČ: 09051368, DIČ: CZ09051368
            </p>
          </div>
          <div className="right">
            <p>
              Datum (DUZP): {longDt(d.taxDate)}
              <br />
              Vystavil: {d.issuedBy}
            </p>
            <h2>Dobropis: {d.number}</h2>
          </div>
        </div>
        <p>
          ID rezervace: <strong>{r.id}</strong>
          <br />
          Platba: {d.paymentLabel}
        </p>
        <p>
          <strong>STORNO {d.kind === 'services' ? 'služeb' : d.kind === 'price_penalty' ? 'pokuty' : ''} u rezervace {r.id}</strong>
        </p>
        <table>
          <thead>
            <tr>
              <th>Popis</th>
              <th className="num">Částka včetně DPH</th>
            </tr>
          </thead>
          <tbody>
            {d.lines.map((l, i) => (
              <tr key={i}>
                <td>{l.title}</td>
                <td className="num">{kc(l.amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th>Celkem:</th>
              <td className="num">{kc(d.total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
